/**
 * Recurring Ticket frequency → display label.
 *
 * All 7 values per GATE2_DECISIONS §8.
 * BUG-02 handling: the backend does NOT validate the `frequency` field, so a row can
 * ship a string outside this enum. UI consumers should fall back to the raw value
 * (title-cased) rather than crashing. See `getFrequencyLabel()` below.
 */

import type { RecurringTicketFrequency } from "shared";

export const frequencyLabelMap: Record<RecurringTicketFrequency, string> = {
  daily: "Daily",
  weekly: "Weekly",
  bi_weekly: "Bi-weekly",
  monthly: "Monthly",
  quarterly: "Quarterly",
  semi_annually: "Semi-annually",
  annually: "Annually",
};

export const frequencyOptions: Array<{
  value: RecurringTicketFrequency;
  label: string;
}> = (Object.entries(frequencyLabelMap) as Array<
  [RecurringTicketFrequency, string]
>).map(([value, label]) => ({ value, label }));

/**
 * Returns the display label for a backend `frequency` string.
 * BUG-02: falls back to a title-cased version of the raw string when the value is
 * not in the enum (so an unknown backend value renders instead of crashing).
 */
export const getFrequencyLabel = (
  frequency: string | null | undefined
): string => {
  if (!frequency) return "—";
  const known = frequencyLabelMap[frequency as RecurringTicketFrequency];
  if (known) return known;
  // BUG-02 fallback: "bi_weekly_unusual" → "Bi Weekly Unusual"
  return frequency
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
};
